import { useState } from 'react';
import {
  ChoiceChips,
  ChoiceList,
  CountStepper,
  DateRangeRow,
  QuestionSheet,
} from '../design-system';
import { CALENDAR_EXIT_MS } from '../design-system/flow/CalendarSheet';
import { HEADCOUNT, LIST_ERROR } from '../../lib/almaFlow/flowConfig';
import type { FlowAction, SheetView } from '../../lib/almaFlow/flow';

/**
 * AlmaFlowSheet — renders the current Phase 1 question from the flow's
 * SheetView using design-system pieces. Every answer dispatches straight to the
 * flow reducer; the only state here is which city's calendar is open.
 */
export interface AlmaFlowSheetProps {
  view: SheetView;
  dispatch: (action: FlowAction) => void;
  today: string;
  onRetry: () => void;
  /** A tab bar sits right below: tighten the sheet's bottom padding. */
  docked?: boolean;
  /** Reopens the previous question. Omit on the first one. */
  onBack?: () => void;
  /** Slides the question in from the left instead of rising (just went back). */
  enter?: 'up' | 'back';
  /** Plays the leave motion (hand-off to Part 2). */
  leaving?: boolean;
}

export function AlmaFlowSheet({ view, dispatch, today, onRetry, onBack, enter, docked = false, leaving = false }: AlmaFlowSheetProps) {
  const error = { message: LIST_ERROR, onRetry };
  // Which city's calendar is open (by position), so finishing one can open the next.
  const [openCity, setOpenCity] = useState<number | null>(null);

  switch (view.step) {
    case 'where_when':
      return (
        <QuestionSheet
          title={view.title}
          helper={view.helper}
          note={view.daysNote || undefined}
          onContinue={view.error ? undefined : () => dispatch({ type: 'completeWhereWhen', today })}
          continueDisabled={!view.canContinue}
          onBack={onBack}
          enter={enter}
          docked={docked}
          leaving={leaving}
        >
          <ChoiceChips
            label={view.title}
            mode="ordered"
            positionWord="stop"
            options={view.options}
            value={view.value}
            onChange={(ids) => dispatch({ type: 'setCities', ids })}
            error={view.error ? error : undefined}
          />
          {view.ranges.length > 0 ? (
            <div>
              {view.ranges.map((range, i) => {
                const next = view.ranges[i + 1];
                return (
                  <DateRangeRow
                    key={`${i}-${range.label}`}
                    label={range.label}
                    from={range.from}
                    to={range.to}
                    min={range.min}
                    locked={range.locked}
                    lockedHint={i > 0 ? `Pick ${view.ranges[i - 1].label} first` : undefined}
                    error={range.error}
                    continued={i > 0}
                    today={today}
                    nextLabel={next && !(next.from && next.to) ? next.label : undefined}
                    open={openCity === i}
                    // Closing only clears this city's own flag.
                    onOpenChange={(open) => setOpenCity((current) => (open ? i : current === i ? null : current))}
                    onChange={(from, to) => dispatch({ type: 'setRange', city: i, from, to })}
                    // Confirmed: once this sheet has retracted, the next city's rises, if it still needs dates.
                    onConfirm={() => {
                      if (next && !(next.from && next.to)) window.setTimeout(() => setOpenCity(i + 1), CALENDAR_EXIT_MS + 40);
                    }}
                  />
                );
              })}
            </div>
          ) : null}
        </QuestionSheet>
      );

    case 'who':
      return (
        <QuestionSheet
          title={view.title}
          onContinue={view.showCount ? () => dispatch({ type: 'completeWho' }) : undefined}
          onBack={onBack}
          enter={enter}
          docked={docked}
          leaving={leaving}
        >
          <ChoiceChips
            label={view.title}
            mode="single"
            options={view.options}
            value={view.value ? [view.value] : []}
            onChange={(ids) => {
              const group = view.options.find((o) => o.id === ids[0])?.id;
              if (group) dispatch({ type: 'pickGroup', group });
            }}
          />
          {view.showCount ? (
            <CountStepper
              label={view.countLabel}
              value={view.count}
              onChange={(size) => dispatch({ type: 'setGroupSize', size })}
              min={HEADCOUNT.min}
              max={HEADCOUNT.max}
              decrementLabel="One fewer person"
              incrementLabel="One more person"
            />
          ) : null}
        </QuestionSheet>
      );

    case 'priorities':
    case 'branch': {
      const isPriorities = view.step === 'priorities';
      return (
        <QuestionSheet
          title={view.title}
          helper={view.helper}
          onContinue={view.error ? undefined : () => dispatch({ type: isPriorities ? 'completePriorities' : 'completeBranch' })}
          continueDisabled={!view.canContinue}
          onBack={onBack}
          enter={enter}
          docked={docked}
          leaving={leaving}
        >
          <ChoiceChips
            label={view.title}
            mode={isPriorities ? 'ordered' : 'multi'}
            options={view.options}
            value={view.value}
            onChange={(ids) => dispatch({ type: isPriorities ? 'setPriorities' : 'setBranchPicks', ids })}
            other={{
              text: view.otherText,
              onTextChange: (text) => dispatch({ type: isPriorities ? 'setPriorityOther' : 'setBranchOther', text }),
            }}
            error={view.error ? error : undefined}
          />
        </QuestionSheet>
      );
    }

    case 'walking':
      return (
        <QuestionSheet title={view.title} helper={view.helper} onBack={onBack} enter={enter} docked={docked} leaving={leaving}>
          {view.error ? (
            <ChoiceChips label={view.title} mode="single" options={[]} value={[]} onChange={() => {}} error={error} />
          ) : (
            <ChoiceList
              label={view.title}
              options={view.options}
              value={null}
              onChange={(id) => dispatch({ type: 'pickWalking', id })}
            />
          )}
        </QuestionSheet>
      );

    default:
      return null;
  }
}

export default AlmaFlowSheet;
